// services/batch.service.ts
import { axiosInstance } from "@/lib/axiosInstance";
import {
  AsyncSelectFetchParams,
  AsyncSelectFetchResult,
} from "@/components/form/types";

// ─── Types ────────────────────────────────────────────────────────────────────

export type BatchStatus = "ongoing" | "completed";

export interface BatchType {
  id: string;
  college_id: string;
  degree_program_id: string;
  name: string;
  start_year: number;
  end_year: number;
  current_semester: number;
  status: BatchStatus;
  is_active: boolean;
  department_id?: string;
  created_at: string;
}

export type BatchParamsType = {
  search?: string;
  dept_id?: string;
  status?: BatchStatus;
  active?: boolean;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  page?: number;
  limit?: number;
};

// ─── Real API: Batches ────────────────────────────────────────────────────────

export const fetchAllBatches = async (filters: BatchParamsType) => {
  const params: BatchParamsType = { active: true, ...filters };
  const res = await axiosInstance.get("/v1/batches", { params });
  const items: BatchType[] = res.data?.data || res.data || [];
  const total = res.data?.total ?? items.length;
  const page = filters.page ?? 1;
  const limit = filters.limit ?? 10;
  return {
    batches: items,
    metaData: {
      pagination: {
        total,
        page,
        limit,
        totalPages: Math.ceil(total / limit),
      },
    },
  };
};

export const fetchBatchById = async (id: string): Promise<BatchType> => {
  const res = await axiosInstance.get(`/v1/batches/${id}`);
  return res.data?.data || res.data;
};

export interface BatchUpdateType {
  name: string;
  start_year: number;
  end_year: number;
}

/** PATCH /v1/batches/:id */
export const updateBatch = async (
  id: string,
  data: BatchUpdateType
): Promise<BatchType> => {
  const res = await axiosInstance.patch(`/v1/batches/${id}`, data);
  return res.data?.data || res.data;
};

/** DELETE /v1/batches/:id */
export const deleteBatch = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/v1/batches/${id}`);
};

/** GET /v1/programs/:programId/batches — all batches for a degree program */
export const fetchProgramBatches = async (
  programId: string
): Promise<BatchType[]> => {
  const res = await axiosInstance.get(`/v1/programs/${programId}/batches`);
  return res.data?.data || res.data || [];
};

// ─── Real API: Program Batches ────────────────────────────────────────────────

export interface ProgramBatchCreateType {
  name: string;
  start_year: number;
  end_year: number;
}

export const createProgramBatch = async (
  programId: string,
  data: ProgramBatchCreateType
) => {
  const res = await axiosInstance.post(
    `/v1/programs/${programId}/batches`,
    data
  );
  return res.data?.data || res.data;
};

export const fetchProgramOptions = async (
  params: AsyncSelectFetchParams
): Promise<AsyncSelectFetchResult> => {
  const { search, page, pageSize } = params;
  const res = await axiosInstance.get("/v1/programs", {
    params: { search, page, limit: pageSize },
  });
  const items = (res.data?.data || res.data || []).map((p: any) => ({
    label: p.name,
    value: p.id,
    subtitle: p.code,
  }));
  return { items, hasMore: items.length === pageSize, total: items.length };
};

export const fetchBatchOptions = async (
  params: AsyncSelectFetchParams
): Promise<AsyncSelectFetchResult> => {
  const { search, page, pageSize } = params;
  const res = await axiosInstance.get("/v1/batches", {
    params: { search, page, limit: pageSize },
  });
  const items: BatchType[] = res.data?.data || res.data || [];
  return {
    items: items.map((b) => ({ label: b.name, value: b.id })),
    hasMore: items.length === pageSize,
    total: items.length,
  };
};

export const updateBatchStatus = async (id: string, status: BatchStatus) => {
  const res = await axiosInstance.patch(`/v1/batches/${id}/status`, { status });
  return res.data?.data || res.data;
};

export const advanceBatchSemester = async (id: string) => {
  const res = await axiosInstance.patch(`/v1/batches/${id}/advance-semester`);
  return res.data?.data || res.data;
};

/** PATCH /v1/batches/:id/semester — set semester to a specific value */
export const updateBatchSemester = async (
  id: string,
  current_semester: number
) => {
  const res = await axiosInstance.patch(`/v1/batches/${id}/semester`, {
    current_semester,
  });
  return res.data?.data || res.data;
};

export const batchService = {
  fetchAllBatches,
  fetchBatchById,
  createProgramBatch,
  fetchProgramOptions,
  fetchBatchOptions,
  updateBatchStatus,
  advanceBatchSemester,
  updateBatchSemester,
};
